<template>
  <DropDownContainer
    v-model="listVisible"
    dropDownMaxWidth="350px"
    @close="handleDropDownClose"
    @open="() => $emit('open')"
  >
    <template v-slot:target>
      <MultiselectSearchButton
        :active="darkenWhenActive && listVisible"
        :class="$style.valueTextContainer"
        :disabled="disabled"
        :includeCaret="includeCaret"
        :listVisible="listVisible"
        :removable="removable"
        :useDarkLinkStyleForButton="useDarkLinkStyleForButton"
        :useLinkStyleForButton="useLinkStyleForButton"
        @click="toggleDropDownVisibility"
        @close="handleCloseClick"
      >
        <template v-if="displayValue">
          <span v-if="name">{{ name }}:</span>
          <span
            :class="$style.valueText"
            :title="valueText"
          >
            <strong>{{ valueText }}</strong>
          </span>
        </template>
        <template v-else>
          {{ name }}
        </template>
      </MultiselectSearchButton>
    </template>
    <template v-slot:dropDown>
      <MultiselectMenu
        :alwaysShowSearch="alwaysShowSearch"
        :errorText="errorText"
        :loading="loading"
        :maxOptionsSelectable="maxOptionsSelectable"
        :multiselect="multiselect"
        :numResultsText="numResultsText"
        :options="options"
        :sortOptions="sortOptions"
        :value="value"
        @input="handleMenuInput"
      >
        <template v-slot:noOptionsAvailableMessage>
          <slot name="noOptionsAvailableMessage" />
        </template>

        <template v-slot:footer>
          <slot name="footer" />
        </template>
      </MultiselectMenu>
    </template>
  </DropDownContainer>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import DropDownContainer from 'components/DropDownMenu/DropDownContainer.vue';
import MultiselectMenu from 'components/DropDownMenu/DropDownMultiSelectSearch/MultiSelectMenu.vue';
import MultiselectSearchButton from 'components/DropDownMenu/DropDownMultiSelectSearch/MultiSelectSearchButton.vue';
import pluralize from 'pluralize';

export default {
  name: 'DropDownMultiSelectSearch',

  components: {
    DropDownContainer,
    MultiselectMenu,
    MultiselectSearchButton,
  },

  mixins: [clickaway],

  props: {
    alwaysShowSearch: {
      type: Boolean,
      default: false,
    },
    darkenWhenActive: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    displayValue: {
      type: Boolean,
      default: true,
    },
    emptyValueText: {
      type: String,
      default: 'all',
    },
    errorText: {
      type: String,
      default: '',
    },
    hideShowingResults: {
      type: Boolean,
      default: false,
    },
    includeCaret: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    maxOptionsSelectable: {
      type: Number,
      default: 0,
    },
    multiselect: {
      type: Boolean,
      default: true,
    },
    name: {
      type: String,
      default: '',
    },
    /* `options` should be an array of objects with a `name` and `value` attribute */
    options: {
      type: Array,
      default: () => [],
    },
    removable: {
      type: Boolean,
      default: false,
    },
    showFullValueText: {
      type: Boolean,
      default: false,
    },
    sortOptions: {
      type: Boolean,
      default: true,
    },
    useDarkLinkStyleForButton: {
      type: Boolean,
      default: false,
    },
    useLinkStyleForButton: {
      type: Boolean,
      default: false,
    },
    /* `value` is an array of the values of the selected options. Supports use of v-model */
    value: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    listVisible: false,
    maxResultsToShow: 500,
    matchingOptions: [],
    searchText: '',
  }),

  computed: {
    availableOptions() {
      const options = (() => {
        if (!this.searchText && !this.multiselect) return this.options;
        if (!this.searchText && this.multiselect) {
          // Only show options that have not been selected
          return this.options.filter(d => !this.value.includes(d.value));
        }
        return this.matchingOptions;
      })();
      if (this.sortOptions) this.sortOptionsByName(options);
      return options;
    },

    noResultsFound() { return Boolean(this.searchText && !this.availableOptions.length); },

    numResultsText() {
      let resultsText = `Showing ${this.options.length} ${this.pluralizedResultText}.`;

      if (this.searchText && !this.hideShowingResults) {
        resultsText = `Showing ${this.matchingOptions.length} of ${this.options.length} ${this.pluralizedResultText}.`;
      } else if (this.availableOptions.length > this.maxResultsToShow && !this.hideShowingResults) {
        resultsText = `Showing ${this.maxResultsToShow} of ${this.options.length} ${this.pluralizedResultText}.`;
      } else if (this.hideShowingResults) {
        resultsText = '';
      }
      return resultsText;
    },

    pluralizedResultText() {
      return pluralize('result', this.options.length);
    },

    selectingDisabled() {
      return !!this.maxOptionsSelectable && this.value.length >= this.maxOptionsSelectable;
    },

    valueText() {
      if (!this.value.length) return this.emptyValueText;
      if (this.value.length < 3) {
        const valueText = this.value
          .map((val) => {
            // Find associated option name if available
            const option = this.options.find(opt => opt.value === val);
            if (option) return option.valueTextName || option.name;
            return val;
          })
          .join(', ');
        if (this.showFullValueText || valueText.length < 24) return valueText;
      }
      return `${this.value.length} values`;
    },
  },

  methods: {
    handleChange(option, checked) {
      let newValue = this.value.slice(0);
      const index = this.value.indexOf(option.value);
      if (index === -1 && checked === true) {
        // Selecting an option; not allowed if option is disabled
        if (this.selectingDisabled) return;
        if (this.multiselect) {
          newValue.push(option.value);
        } else {
          newValue = [option.value];
        }
      } else if (this.multiselect && index > -1 && checked === false) {
        // De-selecting an option; not allowed if option is required
        if (option.required) return;
        newValue.splice(index, 1);
      }
      this.$emit('input', newValue);
    },

    handleCloseClick() {
      this.$emit('remove');
    },

    handleDropDownClose() {
      this.$emit('close');
      this.searchText = '';
    },

    handleMenuInput(newValue) {
      this.$emit('input', newValue);
    },

    sortOptionsByName(options) {
      return [...options].sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    },

    toggleDropDownVisibility() {
      this.listVisible = !this.listVisible;
    },
  },
};
</script>
<style module>
.valueTextContainer {
  --valueTextPosition: var(--valueTextFlex);
  --valueTextAlignItems: var(--valueTextAlignment);
}

.valueText {
  max-width: var(--valueTextMaxWidth);
  overflow: var(--valueTextOverflow);
  padding-left: var(--valueTextPadding);
  text-overflow: var(--valueTextTextOverflow, clip);
  white-space: var(--valueTextWhiteSpace, normal);
}
</style>
